/**
 * Client-side filter for the collection page.
 *
 * Works WITHOUT Search & Discovery app. Reads filters from URL params,
 * hides/shows cards in the grid based on data-x-* attributes emitted by
 * snippets/product-card.liquid, and renders dynamic filter checkboxes
 * derived from the products actually on the page.
 *
 * URL param schema (matches Shopify's native shape so links work both ways):
 *   ?filter.p.vendor=Amouage&filter.p.vendor=Liis     (multi)
 *   ?filter.p.tag=note%3Aamber&filter.p.tag=gender%3Afeminine
 *   ?filter.p.price.gte=50&filter.p.price.lte=300
 *   ?filter.v.availability=in-stock
 *   ?sort_by=created-descending
 */
(function () {
  'use strict';

  // Run on collection pages only
  if (!document.querySelector('.x-plp')) return;

  var grid = document.querySelector('[data-x-plp-grid]') || document.querySelector('.x-plp__grid');
  if (!grid) return;

  var countEl = document.querySelector('[data-x-plp-count]');
  var filterForm = document.querySelector('[data-x-plp-filter-form]') || document.querySelector('#x-plp-filter-form');
  var filterDrawer = document.querySelector('[data-x-plp-filter-drawer]');
  var filterOpenBtn = document.querySelector('[data-x-plp-open-filters]');
  var activeCountEl = document.querySelector('[data-x-plp-active-count]');
  var drawerCountEl = document.querySelector('[data-x-plp-drawer-count]');
  var ACCORD_ORDER = [
    'fresh',
    'gourmand',
    'woody',
    'oud',
    'resin-smoke',
    'floral',
    'boozy',
    'fruity',
    'earthy',
    'aromatic',
    'tea-forward',
    'spiced-aromatic',
    'dark'
  ];
  var ACCORD_LABELS = {
    fresh: 'Fresh',
    gourmand: 'Gourmand',
    woody: 'Woody',
    oud: 'Oud',
    'resin-smoke': 'Resin & Smoke',
    floral: 'Floral',
    boozy: 'Boozy',
    fruity: 'Fruity',
    earthy: 'Earthy',
    aromatic: 'Aromatic',
    'tea-forward': 'Tea',
    'spiced-aromatic': 'Spiced Aromatics',
    dark: 'Dark'
  };

  // Model state, (re)built by boot() AFTER all pages are hydrated into the
  // grid, so client-side search/filter/chips cover the FULL catalog, not just
  // the first server-rendered page.
  var cards = [];
  var products = [];
  var facets = null;

  function formatMoney(cents) {
    var amount = (parseInt(cents || 0, 10) / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return '$' + amount;
  }

  function variantPrice(variant) {
    return parseInt(variant && variant.price ? variant.price : 0, 10) || 0;
  }

  function isSampleVariant(variant) {
    var title = String((variant && (variant.public_title || variant.title || variant.name)) || '').toLowerCase();
    var compact = title.replace(/\s+/g, '').replace(/-/g, '');
    return title.indexOf('sample') !== -1 ||
      title.indexOf('discovery') !== -1 ||
      title.indexOf('vial') !== -1 ||
      title.indexOf('decant') !== -1 ||
      title.indexOf('travel') !== -1 ||
      title.indexOf('tester') !== -1 ||
      title.indexOf('mini') !== -1 ||
      compact.indexOf('0.7ml') !== -1 ||
      compact.indexOf('1ml') !== -1 ||
      compact.indexOf('1.5ml') !== -1 ||
      compact.indexOf('2ml') !== -1 ||
      compact.indexOf('3ml') !== -1;
  }

  function lowestVariant(variants) {
    return (variants || []).reduce(function (best, variant) {
      if (!best) return variant;
      return variantPrice(variant) < variantPrice(best) ? variant : best;
    }, null);
  }

  function entryVariantFromMeta(productMeta) {
    if (!productMeta || !Array.isArray(productMeta.variants) || !productMeta.variants.length) return null;
    var bottles = productMeta.variants.filter(function (variant) { return !isSampleVariant(variant); });
    return lowestVariant(bottles.length ? bottles : productMeta.variants);
  }

  function entryVariantFromProduct(product) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
    var availableBottles = product.variants.filter(function (variant) {
      return variant.available && !isSampleVariant(variant);
    });
    if (availableBottles.length) return lowestVariant(availableBottles);
    var bottles = product.variants.filter(function (variant) { return !isSampleVariant(variant); });
    if (bottles.length) return lowestVariant(bottles);
    var available = product.variants.filter(function (variant) { return variant.available; });
    return lowestVariant(available.length ? available : product.variants);
  }

  function collectionMetaProducts() {
    return (window.meta && Array.isArray(window.meta.products)) ? window.meta.products : [];
  }

  function metaForCard(card) {
    var handle = card.getAttribute('data-product-handle');
    var id = card.getAttribute('data-product-id');
    return collectionMetaProducts().find(function (product) {
      return String(product.handle) === String(handle) || String(product.id) === String(id);
    });
  }

  function applyEntryVariantToCard(card, variant) {
    if (!card || !variant || !variant.price) return;
    var price = parseInt(variant.price, 10);
    if (!price) return;
    var dollars = price / 100;
    card.setAttribute('data-x-price', String(dollars));
    card.setAttribute('data-variant-id', variant.id);
    card.setAttribute('data-product-variant-id', variant.id);
    card.setAttribute('data-x-price-source', 'bottle');
    card.setAttribute('data-x-display-variant-title', variant.public_title || variant.title || variant.name || '');

    var priceRow = card.querySelector('.price-row');
    if (priceRow) {
      var from = priceRow.querySelector('.from');
      if (from) from.remove();
      var priceEl = priceRow.querySelector('.price') || priceRow.appendChild(document.createElement('span'));
      priceEl.className = 'price';
      priceEl.textContent = formatMoney(price);
    }

    card.querySelectorAll('[data-variant-id], [data-product-variant-id]').forEach(function (node) {
      node.setAttribute('data-variant-id', variant.id);
      node.setAttribute('data-product-variant-id', variant.id);
    });
    card.querySelectorAll('input[name="id"]').forEach(function (input) {
      input.value = variant.id;
    });
    card.querySelectorAll('[data-product-price]').forEach(function (node) {
      node.setAttribute('data-product-price', formatMoney(price));
    });
  }

  function correctEntryPrices(scope) {
    Array.from((scope || document).querySelectorAll('.product-card')).forEach(function (card) {
      var productMeta = metaForCard(card);
      var variant = entryVariantFromMeta(productMeta);
      if (variant) applyEntryVariantToCard(card, variant);
    });
  }

  function hydrateEntryPricesFromProducts(scope, maxCards) {
    Array.from((scope || document).querySelectorAll('.product-card'))
      .filter(function (card) { return card.getAttribute('data-x-price-source') !== 'bottle'; })
      .slice(0, maxCards || 32)
      .forEach(function (card) {
        var productUrl = card.getAttribute('data-product-url');
        if (!productUrl) return;
        var jsonUrl = productUrl.split('?')[0].replace(/\/$/, '') + '.js';
        fetch(jsonUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (response) { return response.ok ? response.json() : null; })
          .then(function (product) {
            var variant = entryVariantFromProduct(product);
            if (variant) applyEntryVariantToCard(card, variant);
          })
          .catch(function () {});
      });
  }

  // ─── Parse cards into a lightweight model ───────────────────────────
  function parseCard(card) {
    var handle = card.getAttribute('data-product-handle') || '';
    var allNoteWeights = window.XTRAIT_PRODUCT_NOTE_WEIGHTS || {};
    var noteWeights = Object.assign({}, allNoteWeights[handle] || {});
    var tags = (card.getAttribute('data-x-tags') || '')
      .split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
    var fallbackNoteRank = 0;
    tags.forEach(function (tag) {
      if (tag.indexOf('note:') !== 0) return;
      var note = tag.slice(5);
      if (!noteWeights[note]) {
        noteWeights[note] = Math.max(1, 100 - (fallbackNoteRank * 8));
      }
      fallbackNoteRank++;
    });
    // Perfumer lives in its own asset (x-product-perfumers.js), not in data-x-tags,
    // because the taxonomy file it would otherwise live in is rebuilt nightly.
    var perfMap = window.XTRAIT_PRODUCT_PERFUMERS || {};
    var perfumers = (perfMap[handle] || []).map(function (x) { return x[0]; });
    return {
      el: card,
      handle: handle,
      vendor: (card.getAttribute('data-x-vendor') || '').toLowerCase(),
      tags: tags,
      perfumers: perfumers,
      noteWeights: noteWeights,
      price: parseFloat(card.getAttribute('data-x-price') || '0'),
      available: card.getAttribute('data-x-available') === 'true',
      created: parseInt(card.getAttribute('data-x-created') || '0', 10),
    };
  }
  // ─── Derive filter groups from products ─────────────────────────────
  function derive() {
    var vendors = {};
    var notes = {};
    var genders = {};
    var perfumers = {};
    var perfumerNames = {};
    var minPrice = Infinity, maxPrice = 0;
    products.forEach(function (p) {
      if (p.vendor) vendors[p.vendor] = (vendors[p.vendor] || 0) + 1;
      // Product cards intentionally expose the bottle display price, so the
      // derived range should match what shoppers see on the shelf.
      if (p.price > 0) {
        if (p.price < minPrice) minPrice = p.price;
        if (p.price > maxPrice) maxPrice = p.price;
      }
      (p.perfumers || []).forEach(function (ps) { perfumers[ps] = (perfumers[ps] || 0) + 1; });
      (((window.XTRAIT_PRODUCT_PERFUMERS || {})[p.handle]) || []).forEach(function (x) { perfumerNames[x[0]] = x[1]; });
      p.tags.forEach(function (t) {
        if (t.indexOf('note:') === 0) {
          var n = t.slice(5);
          if (isAccord(n)) notes[n] = (notes[n] || 0) + 1;
        }
        if (t.indexOf('gender:') === 0) {
          var g = t.slice(7);
          genders[g] = (genders[g] || 0) + 1;
        }
      });
    });
    return {
      vendors: vendors, notes: notes, genders: genders,
      perfumers: perfumers, perfumerNames: perfumerNames,
      minPrice: isFinite(minPrice) ? Math.floor(minPrice) : 0,
      maxPrice: maxPrice ? Math.ceil(maxPrice) : 500,
    };
  }

  // ─── URL state helpers ──────────────────────────────────────────────
  function readUrl() {
    var p = new URLSearchParams(window.location.search);
    return {
      vendors: p.getAll('filter.p.vendor').map(function (s) { return s.toLowerCase(); }),
      tags: p.getAll('filter.p.tag').map(function (s) { return decodeURIComponent(s).toLowerCase(); }),
      genders: p.getAll('filter.p.tag')
        .map(function (s) { return decodeURIComponent(s).toLowerCase(); })
        .filter(function (s) { return s.indexOf('gender:') === 0; })
        .map(function (s) { return s.slice(7); }),
      notes: p.getAll('filter.p.tag')
        .map(function (s) { return decodeURIComponent(s).toLowerCase(); })
        .filter(function (s) { return s.indexOf('note:') === 0; })
        .map(function (s) { return s.slice(5); }),
      perfumers: p.getAll('filter.p.tag')
        .map(function (s) { return decodeURIComponent(s).toLowerCase(); })
        .filter(function (s) { return s.indexOf('perfumer:') === 0; })
        .map(function (s) { return s.slice(9); }),
      priceGte: parseFloat(p.get('filter.p.price.gte') || ''),
      priceLte: parseFloat(p.get('filter.p.price.lte') || ''),
      availability: p.get('filter.v.availability') || null,
      sort: p.get('sort_by') || null,
    };
  }

  function setUrl(updates) {
    var p = new URLSearchParams(window.location.search);
    Object.keys(updates).forEach(function (key) {
      var val = updates[key];
      p.delete(key);
      if (Array.isArray(val)) val.forEach(function (v) { p.append(key, v); });
      else if (val !== null && val !== undefined && val !== '') p.set(key, val);
    });
    var qs = p.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '');
    window.history.replaceState(null, '', newUrl);
  }

  // Live text search, typing in the [data-x-plp-search] input narrows by
  // product title / vendor / note tags in real time.
  var searchQuery = '';
  function parseCardText(card) {
    var bits = [];
    var title = card.querySelector('.product-card__title, .product-card__name');
    var brand = card.querySelector('.product-card__brand, .product-card__vendor');
    if (title) bits.push(title.textContent);
    if (brand) bits.push(brand.textContent);
    bits.push(card.getAttribute('data-x-vendor') || '');
    bits.push(card.getAttribute('data-x-tags') || '');
    return bits.join(' ').toLowerCase();
  }
  // ─── Apply filters ──────────────────────────────────────────────────
  function applyFilters() {
    var state = readUrl();
    var shown = 0;
    var q = (searchQuery || '').trim().toLowerCase();
    products.forEach(function (p) {
      var ok = true;
      if (state.vendors.length && state.vendors.indexOf(p.vendor) === -1) ok = false;
      if (ok && state.genders.length) {
        var hasGender = state.genders.some(function (g) { return p.tags.indexOf('gender:' + g) !== -1; });
        if (!hasGender) ok = false;
      }
      if (ok && state.notes.length) {
        var hasNote = state.notes.some(function (n) { return p.tags.indexOf('note:' + n) !== -1; });
        if (!hasNote) ok = false;
      }
      if (ok && state.perfumers.length) {
        var hasPerfumer = state.perfumers.some(function (x) { return (p.perfumers || []).indexOf(x) !== -1; });
        if (!hasPerfumer) ok = false;
      }
      if (ok && !isNaN(state.priceGte) && p.price < state.priceGte) ok = false;
      if (ok && !isNaN(state.priceLte) && p.price > state.priceLte) ok = false;
      if (ok && state.availability === 'in-stock' && !p.available) ok = false;
      if (ok && state.availability === 'out-of-stock' && p.available) ok = false;
      // Live search across title + brand + tags
      if (ok && q) {
        var matched = q.split(/\s+/).every(function (tok) {
          return tok === '' || p.searchText.indexOf(tok) !== -1;
        });
        if (!matched) ok = false;
      }
      p.el.style.display = ok ? '' : 'none';
      if (ok) shown++;
    });
    if (countEl) countEl.textContent = shown;
    syncMobileChrome(state, shown);
    // Apply sort. When a shopper filters by notes, default to the closest
    // Fragrantica-style note match instead of raw theme order.
    if (state.notes.length && (!state.sort || state.sort === 'manual')) applyNoteRelevanceSort(state);
    else if (state.sort) applySort(state.sort);
    // Re-render chips + brand bar
    renderChips(state);
    renderBrandBar(state);
    // #63, annotate each visible card with the active filters it matched
    renderMatchTags(state);
  }

  function isMobileFilters() {
    return window.matchMedia && window.matchMedia('(max-width: 960px)').matches;
  }

  function isPriceActive(state) {
    return !isNaN(state.priceGte) || !isNaN(state.priceLte);
  }

  function activeFilterCount(state) {
    return state.vendors.length +
      state.genders.length +
      state.notes.length +
      state.perfumers.length +
      (state.availability ? 1 : 0) +
      (isPriceActive(state) ? 1 : 0);
  }

  function groupOpenAttr(isActive, desktopDefaultOpen) {
    if (isActive || (!isMobileFilters() && desktopDefaultOpen)) return ' open';
    return '';
  }

  function syncMobileChrome(state, shown) {
    var count = activeFilterCount(state);
    if (activeCountEl) {
      activeCountEl.textContent = count;
      activeCountEl.hidden = !count;
    }
    if (drawerCountEl) drawerCountEl.textContent = shown;
  }

  function openFilters() {
    document.body.classList.add('x-plp-filters-open');
    if (filterDrawer) filterDrawer.setAttribute('aria-modal', 'true');
    if (filterOpenBtn) filterOpenBtn.setAttribute('aria-expanded', 'true');
  }

  function closeFilters() {
    document.body.classList.remove('x-plp-filters-open');
    if (filterDrawer) filterDrawer.removeAttribute('aria-modal');
    if (filterOpenBtn) filterOpenBtn.setAttribute('aria-expanded', 'false');
  }

  // ─── #63: accord tags on each card ──────────────────────────────────
  // When any filter is active, show a small translucent line of the product's
  // ACCORDS between the title and price (never the brand, the card already
  // shows the vendor as its kicker, so echoing it read as "brand twice").
  // Actively-filtered accords are listed first.
  function renderMatchTags(state) {
    var anyActive = state.vendors.length || state.genders.length || state.notes.length;
    products.forEach(function (p) {
      var meta = p.el.querySelector('.product-meta');
      if (!meta) return;
      var el = meta.querySelector('.product-card__match');
      if (!anyActive || p.el.style.display === 'none') { if (el) el.remove(); return; }
      var own = p.tags
        .filter(function (t) { return t.indexOf('note:') === 0; })
        .map(function (t) { return t.slice(5); })
        .filter(isAccord);
      if (!own.length) { if (el) el.remove(); return; }
      // matched accords first, then the rest in the shop's canonical order.
      var ownSorted = ACCORD_ORDER.filter(function (n) { return own.indexOf(n) !== -1; });
      var ordered = state.notes.filter(function (n) { return ownSorted.indexOf(n) !== -1; })
        .concat(ownSorted.filter(function (n) { return state.notes.indexOf(n) === -1; }));
      var labels = ordered.slice(0, 5).map(accordLabel);
      if (!el) {
        el = document.createElement('div');
        el.className = 'product-card__match';
        meta.insertBefore(el, meta.querySelector('.price-row'));
      }
      el.textContent = labels.join(' · ');
    });
  }

  function renderBrandBar(state) {
    var host = document.querySelector('[data-x-plp-brandbar]');
    if (!host) return;
    var vendors = Object.keys(facets.vendors).sort();
    if (!vendors.length) { host.innerHTML = ''; return; }
    var html = vendors.map(function (v) {
      var on = state.vendors.indexOf(v) !== -1;
      return '<button type="button" class="x-plp__brand-chip ' + (on ? 'is-active' : '') + '" data-x-brand-toggle="' + v + '">' +
        titleCase(v) +
        '<span class="x-plp__brand-chip-count">' + facets.vendors[v] + '</span>' +
      '</button>';
    }).join('');
    host.innerHTML = html;
  }

  function noteWeight(product, note) {
    return parseFloat((product.noteWeights || {})[note] || 0) || 0;
  }

  function noteRelevance(product, notes) {
    var best = 0;
    var total = 0;
    var matches = 0;
    notes.forEach(function (note) {
      var weight = noteWeight(product, note);
      if (!weight) return;
      matches++;
      total += weight;
      if (weight > best) best = weight;
    });
    return best + (matches * 2) + (total / 100);
  }

  function applyNoteRelevanceSort(state) {
    var visible = products.filter(function (p) { return p.el.style.display !== 'none'; });
    visible.sort(function (a, b) {
      var diff = noteRelevance(b, state.notes) - noteRelevance(a, state.notes);
      if (Math.abs(diff) > 0.001) return diff;
      if (b.created !== a.created) return b.created - a.created;
      return (a.el.querySelector('.name') || {textContent: ''}).textContent.localeCompare((b.el.querySelector('.name') || {textContent: ''}).textContent);
    }).forEach(function (p) { grid.appendChild(p.el); });
  }

  function applySort(sortBy) {
    var visible = products.filter(function (p) { return p.el.style.display !== 'none'; });
    var sorters = {
      'best-selling':       function (a, b) { return b.created - a.created; }, // fallback to newest
      'created-descending': function (a, b) { return b.created - a.created; },
      'created-ascending':  function (a, b) { return a.created - b.created; },
      'price-ascending':    function (a, b) { return a.price - b.price; },
      'price-descending':   function (a, b) { return b.price - a.price; },
      'title-ascending':    function (a, b) { return (a.el.querySelector('.name') || {textContent: ''}).textContent.localeCompare((b.el.querySelector('.name') || {textContent: ''}).textContent); },
      'manual':             function () { return 0; },
    };
    var fn = sorters[sortBy] || sorters['created-descending'];
    visible.sort(fn).forEach(function (p) { grid.appendChild(p.el); });
  }

  // ─── Render filter sidebar from derived facets ──────────────────────
  function renderSidebar() {
    if (!filterForm) return;
    var state = readUrl();
    var html = '';

    // Brand, same filter.p.vendor state as the House Index rail.
    var vKeys = Object.keys(facets.vendors).sort();
    if (vKeys.length > 0) {
      html += '<details class="x-plp__filter-group"' + groupOpenAttr(state.vendors.length > 0, true) + '><summary class="x-plp__filter-title">Brand</summary><div class="x-plp__filter-rows">';
      vKeys.forEach(function (v) {
        var on = state.vendors.indexOf(v) !== -1;
        html += '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
          '<input type="checkbox" data-x-filter="vendor" value="' + escapeAttr(v) + '" ' + (on ? 'checked' : '') + '>' +
          '<span class="x-plp__filter-label">' + escapeHtml(titleCase(v)) + '</span>' +
          '<span class="x-plp__filter-count">' + facets.vendors[v] + '</span>' +
          '</label>';
      });
      html += '</div></details>';
    }

    // Gender (always show, pinned top)
    var gKeys = Object.keys(facets.genders).sort();
    if (gKeys.length > 0) {
      html += '<details class="x-plp__filter-group" open><summary class="x-plp__filter-title">Gender</summary><div class="x-plp__filter-rows">';
      gKeys.forEach(function (g) {
        var on = state.genders.indexOf(g) !== -1;
        html += '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
          '<input type="checkbox" data-x-filter="gender" value="' + g + '" ' + (on ? 'checked' : '') + '>' +
          '<span class="x-plp__filter-label">' + cap(g) + '</span>' +
          '<span class="x-plp__filter-count">' + facets.genders[g] + '</span>' +
          '</label>';
      });
      html += '</div></details>';
    } else {
      // Static fallback if no gender tags exist yet
      html += '<details class="x-plp__filter-group" open><summary class="x-plp__filter-title">Gender</summary><div class="x-plp__filter-rows">' +
        ['feminine','masculine','unisex'].map(function (g) {
          var on = state.genders.indexOf(g) !== -1;
          return '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
            '<input type="checkbox" data-x-filter="gender" value="' + g + '" ' + (on ? 'checked' : '') + '>' +
            '<span class="x-plp__filter-label">' + cap(g) + '</span>' +
            '</label>';
        }).join('') +
        '</div></details>';
    }

    // Perfumer (the "nose"), Kevin 2026-09-25. Source: Parfumo, via
    // assets/x-product-perfumers.js. Only names on >= XTRAIT_PERFUMER_MIN_COUNT
    // products become facets: a list of 300 one-product names is unusable, and a
    // single-product filter is just that product's page with extra steps.
    var minCount = window.XTRAIT_PERFUMER_MIN_COUNT || 2;
    var pKeys = Object.keys(facets.perfumers || {})
      .filter(function (k) { return facets.perfumers[k] >= minCount || state.perfumers.indexOf(k) !== -1; })
      .sort(function (a, b) {
        var d = facets.perfumers[b] - facets.perfumers[a];
        return d !== 0 ? d : (facets.perfumerNames[a] || a).localeCompare(facets.perfumerNames[b] || b);
      });
    if (pKeys.length > 0) {
      html += '<details class="x-plp__filter-group"' + groupOpenAttr(state.perfumers.length > 0, false) + '><summary class="x-plp__filter-title">Perfumer</summary><div class="x-plp__filter-rows x-plp__filter-rows--scroll">';
      pKeys.forEach(function (k) {
        var on = state.perfumers.indexOf(k) !== -1;
        html += '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
          '<input type="checkbox" data-x-filter="perfumer" value="' + escapeAttr(k) + '" ' + (on ? 'checked' : '') + '>' +
          '<span class="x-plp__filter-label">' + escapeHtml(facets.perfumerNames[k] || titleCase(k.replace(/-/g, ' '))) + '</span>' +
          '<span class="x-plp__filter-count">' + (facets.perfumers[k] || 0) + '</span>' +
          '</label>';
      });
      html += '</div></details>';
    }

    // Accords, open by default. Raw product notes stay in product copy; the
    // shopping filter uses broad top accords so customers are not misled by
    // one-off materials.
    var nKeys = ACCORD_ORDER.filter(function (n) { return facets.notes[n]; });
    if (nKeys.length > 0) {
      html += '<details class="x-plp__filter-group"' + groupOpenAttr(state.notes.length > 0, true) + '><summary class="x-plp__filter-title">Accords</summary><div class="x-plp__filter-rows">';
      nKeys.forEach(function (n) {
        var on = state.notes.indexOf(n) !== -1;
        html += '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
          '<input type="checkbox" data-x-filter="note" value="' + n + '" ' + (on ? 'checked' : '') + '>' +
          '<span class="x-plp__filter-label">' + escapeHtml(accordLabel(n)) + '</span>' +
          '<span class="x-plp__filter-count">' + facets.notes[n] + '</span>' +
          '</label>';
      });
      html += '</div></details>';
    }

    // Availability: below Notes, above Price.
    html += '<details class="x-plp__filter-group"' + groupOpenAttr(!!state.availability, true) + '><summary class="x-plp__filter-title">Availability</summary><div class="x-plp__filter-rows">' +
      '<label class="x-plp__filter-row ' + (state.availability === 'in-stock' ? 'is-active' : '') + '">' +
        '<input type="radio" name="x-availability" data-x-filter="availability" value="in-stock" ' + (state.availability === 'in-stock' ? 'checked' : '') + '>' +
        '<span class="x-plp__filter-label">In stock</span>' +
      '</label>' +
      '<label class="x-plp__filter-row ' + (state.availability === 'out-of-stock' ? 'is-active' : '') + '">' +
        '<input type="radio" name="x-availability" data-x-filter="availability" value="out-of-stock" ' + (state.availability === 'out-of-stock' ? 'checked' : '') + '>' +
        '<span class="x-plp__filter-label">Out of stock</span>' +
      '</label>' +
      (state.availability ? '<button type="button" class="x-plp__filter-clear" data-x-clear="availability">Clear</button>' : '') +
      '</div></details>';

    // Price, kept at the bottom of the filter panel.
    html += '<details class="x-plp__filter-group"' + groupOpenAttr(isPriceActive(state), true) + '><summary class="x-plp__filter-title">Price</summary><div class="x-plp__filter-rows">' +
      '<div class="x-plp__price">' +
        '<div class="x-plp__price-inputs">' +
          '<input class="x-plp__price-input" type="number" placeholder="Min" min="0" value="' + (isNaN(state.priceGte) ? '' : state.priceGte) + '" data-x-filter="priceGte">' +
          '<span class="x-plp__price-sep">to</span>' +
          '<input class="x-plp__price-input" type="number" placeholder="Max" min="0" value="' + (isNaN(state.priceLte) ? '' : state.priceLte) + '" data-x-filter="priceLte">' +
        '</div>' +
        '<button type="button" class="x-plp__price-apply" data-x-apply-price>Apply</button>' +
        '<div class="x-plp__price-range">$' + facets.minPrice + ' – $' + facets.maxPrice + '</div>' +
      '</div></div></details>';

    // Clear all
    var anyActive = state.vendors.length || state.tags.length || state.availability || !isNaN(state.priceGte) || !isNaN(state.priceLte);
    if (anyActive) {
      html += '<button type="button" class="x-plp__filter-clear" data-x-clear-all>Clear all filters</button>';
    }

    filterForm.innerHTML = html;
  }

  function renderChips(state) {
    var chipsHost = document.querySelector('[data-x-plp-chips]');
    if (!chipsHost) return;
    var html = '';
    state.vendors.forEach(function (v) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="vendor" data-x-chip-value="' + v + '">' + titleCase(v) + ' ×</button>';
    });
    state.genders.forEach(function (g) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="gender" data-x-chip-value="' + g + '">' + cap(g) + ' ×</button>';
    });
    state.notes.forEach(function (n) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="note" data-x-chip-value="' + n + '">' + escapeHtml(accordLabel(n)) + ' ×</button>';
    });
    state.perfumers.forEach(function (x) {
      var label = (facets && facets.perfumerNames && facets.perfumerNames[x]) || titleCase(x.replace(/-/g, ' '));
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="perfumer" data-x-chip-value="' + escapeAttr(x) + '">' + escapeHtml(label) + ' ×</button>';
    });
    if (!isNaN(state.priceGte) || !isNaN(state.priceLte)) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="price">$' +
        (isNaN(state.priceGte) ? facets.minPrice : state.priceGte) + ' – $' +
        (isNaN(state.priceLte) ? facets.maxPrice : state.priceLte) + ' ×</button>';
    }
    if (state.availability) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="availability">' +
        (state.availability === 'in-stock' ? 'In stock' : 'Out of stock') + ' ×</button>';
    }
    chipsHost.innerHTML = html;
  }

  // ─── Event handlers ─────────────────────────────────────────────────
  function onChange(e) {
    var input = e.target.closest('[data-x-filter]');
    if (!input) return;
    var type = input.getAttribute('data-x-filter');
    var val = input.value.toLowerCase();
    var state = readUrl();

    if (type === 'gender') {
      var arr = state.genders.slice();
      var i = arr.indexOf(val);
      if (input.checked) { if (i === -1) arr.push(val); }
      else { if (i !== -1) arr.splice(i, 1); }
      var notesTags = state.notes.map(function (n) { return 'note:' + n; });
      var genderTags = arr.map(function (g) { return 'gender:' + g; });
      var perfTags = state.perfumers.map(function (x) { return 'perfumer:' + x; });
      setUrl({ 'filter.p.tag': genderTags.concat(notesTags).concat(perfTags) });
    } else if (type === 'note') {
      var arr = state.notes.slice();
      var i = arr.indexOf(val);
      if (input.checked) { if (i === -1) arr.push(val); }
      else { if (i !== -1) arr.splice(i, 1); }
      var noteTags = arr.map(function (n) { return 'note:' + n; });
      var genderTags = state.genders.map(function (g) { return 'gender:' + g; });
      var perfTags = state.perfumers.map(function (x) { return 'perfumer:' + x; });
      setUrl({ 'filter.p.tag': genderTags.concat(noteTags).concat(perfTags) });
    } else if (type === 'perfumer') {
      var arr = state.perfumers.slice();
      var i = arr.indexOf(val);
      if (input.checked) { if (i === -1) arr.push(val); }
      else { if (i !== -1) arr.splice(i, 1); }
      setUrl({ 'filter.p.tag': state.genders.map(function (g) { return 'gender:' + g; })
        .concat(state.notes.map(function (n) { return 'note:' + n; }))
        .concat(arr.map(function (x) { return 'perfumer:' + x; })) });
    } else if (type === 'vendor') {
      var arr = state.vendors.slice();
      var i = arr.indexOf(val);
      if (input.checked) { if (i === -1) arr.push(val); }
      else { if (i !== -1) arr.splice(i, 1); }
      setUrl({ 'filter.p.vendor': arr });
    } else if (type === 'availability') {
      setUrl({ 'filter.v.availability': input.checked ? val : '' });
    } else if (type === 'priceGte' || type === 'priceLte') {
      return; // applied on button click
    }
    applyFilters();
    renderSidebar(); // re-render to update counts/active state
  }

  function onClick(e) {
    if (e.target.closest('[data-x-plp-open-filters]')) {
      e.preventDefault();
      openFilters();
      return;
    }
    if (e.target.closest('[data-x-plp-close-filters]')) {
      e.preventDefault();
      e.stopPropagation();
      closeFilters();
      return;
    }
    var brandToggle = e.target.closest('[data-x-brand-toggle]');
    if (brandToggle) {
      var vendor = (brandToggle.getAttribute('data-x-brand-toggle') || '').toLowerCase();
      var state = readUrl();
      var arr = state.vendors.slice();
      var idx = arr.indexOf(vendor);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(vendor);
      setUrl({ 'filter.p.vendor': arr });
      applyFilters();
      renderSidebar();
      return;
    }
    if (e.target.closest('[data-x-apply-price]')) {
      var gte = document.querySelector('[data-x-filter="priceGte"]');
      var lte = document.querySelector('[data-x-filter="priceLte"]');
      setUrl({
        'filter.p.price.gte': gte && gte.value ? gte.value : '',
        'filter.p.price.lte': lte && lte.value ? lte.value : '',
      });
      applyFilters();
      renderSidebar();
      return;
    }
    var chip = e.target.closest('[data-x-chip-remove]');
    if (chip) {
      var type = chip.getAttribute('data-x-chip-remove');
      var val = (chip.getAttribute('data-x-chip-value') || '').toLowerCase();
      var state = readUrl();
      if (type === 'vendor') setUrl({ 'filter.p.vendor': state.vendors.filter(function (v) { return v !== val; }) });
      else if (type === 'gender') {
        var g = state.genders.filter(function (x) { return x !== val; });
        setUrl({ 'filter.p.tag': g.map(function (x) { return 'gender:' + x; }).concat(state.notes.map(function (x) { return 'note:' + x; })).concat(state.perfumers.map(function (x) { return 'perfumer:' + x; })) });
      }
      else if (type === 'note') {
        var n = state.notes.filter(function (x) { return x !== val; });
        setUrl({ 'filter.p.tag': state.genders.map(function (x) { return 'gender:' + x; }).concat(n.map(function (x) { return 'note:' + x; })).concat(state.perfumers.map(function (x) { return 'perfumer:' + x; })) });
      }
      else if (type === 'perfumer') {
        var pf = state.perfumers.filter(function (x) { return x !== val; });
        setUrl({ 'filter.p.tag': state.genders.map(function (x) { return 'gender:' + x; }).concat(state.notes.map(function (x) { return 'note:' + x; })).concat(pf.map(function (x) { return 'perfumer:' + x; })) });
      }
      else if (type === 'price') setUrl({ 'filter.p.price.gte': '', 'filter.p.price.lte': '' });
      else if (type === 'availability') setUrl({ 'filter.v.availability': '' });
      applyFilters();
      renderSidebar();
      return;
    }
    if (e.target.closest('[data-x-clear-all]') || e.target.closest('[data-x-clear="availability"]')) {
      window.history.replaceState(null, '', window.location.pathname);
      applyFilters();
      renderSidebar();
      return;
    }
  }

  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function titleCase(s) { return s.replace(/\b\w/g, function (c) { return c.toUpperCase(); }); }
  function isAccord(note) {
    return Object.prototype.hasOwnProperty.call(ACCORD_LABELS, String(note || ''));
  }
  function accordLabel(note) {
    return ACCORD_LABELS[String(note || '')] || cap(String(note || '').replace(/-/g, ' '));
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // Wire up
  document.addEventListener('change', onChange);
  document.addEventListener('click', onClick);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeFilters();
  });

  // Live search, debounced text input across product title/brand/tags
  var searchInput = document.querySelector('[data-x-plp-search]');
  if (searchInput) {
    var searchT = null;
    searchInput.addEventListener('input', function (e) {
      clearTimeout(searchT);
      searchT = setTimeout(function () {
        searchQuery = e.target.value || '';
        applyFilters();
      }, 120);
    });
    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.target.value = ''; searchQuery = ''; applyFilters(); }
    });
  }

  // ─── Boot: build the model from every card now in the grid, then render ─
  function boot() {
    correctEntryPrices(grid);
    hydrateEntryPricesFromProducts(grid, 80);
    cards = Array.from(grid.querySelectorAll('.product-card'));
    products = cards.map(parseCard);
    products.forEach(function (p) { p.searchText = parseCardText(p.el); });
    facets = derive();
    renderSidebar();
    applyFilters();
  }

  // ─── Instant pre-boot filter ──────────────────────────────────────────
  // When the page arrives with URL filters (hero "Shop Roja" links, note
  // circles, brand chips), apply them to the server-rendered cards
  // immediately instead of waiting for full-catalog hydration. The complete
  // applyFilters() pass re-runs after boot and refines the result.
  function cardMatchesUrl(card, state) {
    if (state.vendors.length && state.vendors.indexOf((card.getAttribute('data-x-vendor') || '').toLowerCase()) === -1) return false;
    var tagList = (card.getAttribute('data-x-tags') || '').toLowerCase()
      .split(',').map(function (t) { return t.trim(); });
    if (state.genders.length && !state.genders.some(function (g) { return tagList.indexOf('gender:' + g) !== -1; })) return false;
    if (state.notes.length && !state.notes.some(function (n) { return tagList.indexOf('note:' + n) !== -1; })) return false;
    if (state.perfumers.length) {
      var cardPerf = ((window.XTRAIT_PRODUCT_PERFUMERS || {})[card.getAttribute('data-product-handle') || ''] || [])
        .map(function (x) { return x[0]; });
      if (!state.perfumers.some(function (x) { return cardPerf.indexOf(x) !== -1; })) return false;
    }
    var price = parseFloat(card.getAttribute('data-x-price') || '0');
    if (!isNaN(state.priceGte) && price < state.priceGte) return false;
    if (!isNaN(state.priceLte) && price > state.priceLte) return false;
    if (state.availability === 'in-stock' && card.getAttribute('data-x-available') !== 'true') return false;
    if (state.availability === 'out-of-stock' && card.getAttribute('data-x-available') === 'true') return false;
    return true;
  }

  function preFilter(scope) {
    var state = readUrl();
    var active = state.vendors.length || state.notes.length || state.genders.length ||
      state.availability || !isNaN(state.priceGte) || !isNaN(state.priceLte);
    if (!active) return;
    Array.from((scope || grid).querySelectorAll('.product-card')).forEach(function (card) {
      card.style.display = cardMatchesUrl(card, state) ? '' : 'none';
    });
  }

  // ─── Hydration loading state ──────────────────────────────────────────
  var hydrationStatus = null;
  function showHydration() {
    if (hydrationStatus || !grid.parentNode) return;
    hydrationStatus = document.createElement('div');
    hydrationStatus.className = 'x-plp__hydration';
    hydrationStatus.setAttribute('role', 'status');
    hydrationStatus.innerHTML =
      '<span class="x-plp__hydration-spinner" aria-hidden="true"></span>' +
      '<span data-x-hydration-copy>Loading the full shelf…</span>';
    grid.parentNode.insertBefore(hydrationStatus, grid.nextSibling);
    grid.setAttribute('aria-busy', 'true');
  }
  function updateHydration(have, total) {
    if (!hydrationStatus) return;
    var copy = hydrationStatus.querySelector('[data-x-hydration-copy]');
    if (copy) copy.textContent = 'Loading the full shelf, ' + Math.min(have, total) + ' of ' + total;
  }
  function hideHydration() {
    if (hydrationStatus) { hydrationStatus.remove(); hydrationStatus = null; }
    grid.removeAttribute('aria-busy');
  }

  // ─── Hydrate every page into the grid before booting ─────────────────
  // The grid is server-paginated (Shopify caps a page at 50 products) but all
  // filtering/search runs client-side over the DOM. Pages 2..N are fetched IN
  // PARALLEL (the old sequential chain took seconds on the full catalog) and
  // appended in order once all arrive.
  function hydrateAll(done) {
    var total = parseInt(grid.getAttribute('data-x-plp-total') || '0', 10);
    var have = grid.querySelectorAll('.product-card').length;
    if (!total || have >= total) { done(); return; }
    var perPage = have || 50;
    var lastPage = Math.min(Math.ceil(total / perPage), 25);
    if (lastPage < 2) { done(); return; }
    showHydration();
    updateHydration(have, total);

    var path = window.location.pathname;
    var slots = {};
    var pending = 0;
    var loaded = have;

    function finish() {
      for (var page = 2; page <= lastPage; page++) {
        var html = slots[page];
        if (!html) continue;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var more = doc.querySelectorAll('[data-x-plp-grid] .product-card, .x-plp__grid .product-card');
        more.forEach(function (c) { grid.appendChild(document.importNode(c, true)); });
      }
      preFilter(grid);
      done();
    }

    for (var page = 2; page <= lastPage; page++) {
      (function (page) {
        pending++;
        var params = new URLSearchParams(window.location.search);
        params.set('page', page);
        fetch(path + '?' + params.toString())
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) { slots[page] = html || ''; loaded += perPage; })
          .catch(function () { slots[page] = ''; })
          .then(function () {
            updateHydration(loaded, total);
            pending--;
            if (pending === 0) finish();
          });
      })(page);
    }
  }

  // Hide the server pagination nav, client-side now owns the full list.
  var pager = document.querySelector('.x-plp__pagination');
  if (pager) pager.style.display = 'none';

  // Correct the first visible page immediately; full-catalog hydration can take
  // a moment, and prices should not flash full-bottle values while it runs.
  // preFilter() makes URL-filtered arrivals (hero Shop links) instant.
  preFilter(grid);
  correctEntryPrices(grid);
  hydrateEntryPricesFromProducts(grid, 50);
  hydrateAll(function () {
    boot();
    hideHydration();
  });
})();
